import React, { useEffect, useState } from "react";
import { Modal, Button, Spin } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  create_user_contact_action,
  update_user_contact_action,
  fetch_user_contact_details_action,
} from "../_redux/action";
import InputField from "./InputField";
import CompanySearchBasic from "./Company";
import CreateCompany from "./CreateCompany";
import CategorySearchBasic from "./Category";
import CreateCategory from "./CreateCategory";

const ContactModal = ({
  contactModalOpen,
  handleContactModalOpen,
  contactId,
}) => {
  const [loading, setLoading] = useState(false);
  const [contactForm, setContactForm] = useState(null);
  const [selectOtherCompany, setSelectOtherCompany] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [selectOtherCategory, setSelectOtherCategory] = useState(false);

  const dispatch = useDispatch();
  const { contactData } = useSelector(
    (state) => state.fetch_user_contact_details_reducer
  );
  useEffect(() => {
    const callContactApi = async () => {
      if (contactId) {
        setLoading(true);
        await dispatch(fetch_user_contact_details_action(contactId));
        setLoading(false);
      }
    };
    callContactApi();
  }, [contactId, dispatch]);

  useEffect(() => {
    if (!contactId) {
      const msOffice = window.Office;
      msOffice
        .onReady()
        .then(() => {
          const item = msOffice.context.mailbox.item;
          if (item.itemType === msOffice.MailboxEnums.ItemType.Message) {
            const outlookSender = item.from;

            if (outlookSender) {
              setContactForm({
                ...contactForm,
                name: outlookSender.displayName,
                email: outlookSender.emailAddress,
              });
            }
          } else {
            console.error("Selected item is not an email message.");
          }
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }, []);

  useEffect(() => {
    if (contactData) {
      setContactForm(contactData);
    }
  }, [contactData]);

  // Handles input change and clears validation errors
  const onInputChange = async (e) => {
    setContactForm({ ...contactForm, [e.target.name]: e.target.value });

    // Clear the error for the field being updated
    if (formErrors[e.target.name]) {
      setFormErrors({ ...formErrors, [e.target.name]: null });
    }
  };

  // Validates the form input fields
  const validateForm = () => {
    let errors = {};
    if (!contactForm?.name) errors.name = "Name is required";
    if (!contactForm?.email) errors.email = "Email is required";
    if (!contactForm?.contact_number)
      errors.contact_number = "Contact number is required";
    if (!contactForm?.company_id) errors.company_id = "Company is required";
    if (!contactForm?.job_title) errors.job_title = "Title is required";

    // Email validation
    if (
      contactForm?.email &&
      !/^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/.test(contactForm.email)
    ) {
      errors.email = "Please enter a valid email address";
    }

    // Contact number validation (e.g., 10-15 digits)
    if (
      contactForm?.contact_number &&
      !/^\d{10,15}$/.test(contactForm.contact_number)
    ) {
      errors.contact_number =
        "Please enter a valid contact number (10-15 digits)";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0; // If no errors, return true
  };

  const onContactSave = async () => {
    if (!validateForm()) return; // Exit if the form is invalid
    let newContact = null;
    if (contactForm?._id) {
      newContact = await dispatch(
        update_user_contact_action(contactForm._id, contactForm)
      );
    } else {
      newContact = await dispatch(create_user_contact_action(contactForm));
    }

    if (newContact) {
      setContactForm({});
      setSelectOtherCompany(false);
      handleContactModalOpen({});
    } else {
      setFormErrors({
        submit_error: "Failed to save contact, email or user already exists",
      });
    }
  };

  const handleCompanyNotListed = (e) => {
    setSelectOtherCompany(e);
  };

  const onCompanyChange = (e) => {
    setContactForm({
      ...contactForm,
      company_id: e?._id,
    });
    setSelectOtherCompany(false);
  };

  const handleCategoryNotListed = (e) => {
    setSelectOtherCategory(e);
  };

  const onCategoryChange = (e) => {
    if (e?._id) {
      const allCategoryIds = contactForm.category_ids || [];
      allCategoryIds.push(e?._id);
      setContactForm({ ...contactForm, category_ids: allCategoryIds });
    }
    setSelectOtherCategory(false);
  };

  return (
    <Modal
      open={contactModalOpen}
      onCancel={() => handleContactModalOpen({})}
      footer={[
        <Button className="p-4 font-medium" key="save" onClick={onContactSave}>
          Save Contact
        </Button>,
      ]}
      centered
      title={
        contactForm?.name ? `Edit ${contactForm?.name}` : "Add New Contact"
      }
    >
      {loading ? (
        <div className="centered spinner-box">
          <Spin size="large" />
        </div>
      ) : (
        <>
          <InputField
            id="contact-name"
            type="text"
            key={contactForm?.name || "name"}
            name={"name"}
            placeholder="Enter name"
            defaultValue={contactForm?.name}
            onBlur={onInputChange}
            containerClassName="flex flex-col"
            inputClassName="p-2 border my-2 rounded bg-transparent"
            errorClassName="text-xs"
            error={formErrors.name}
          />
          <InputField
            id="contact-email"
            type="text"
            key={contactForm?.email || "email"}
            name={"email"}
            placeholder="Enter email"
            defaultValue={contactForm?.email}
            onBlur={onInputChange}
            containerClassName="flex flex-col"
            inputClassName="p-2 border my-2 rounded bg-transparent"
            error={formErrors.email}
            errorClassName="text-xs"
          />
          <InputField
            id="contact-number"
            type="tel"
            key={contactForm?.contact_number || "contact_number"}
            name={"contact_number"}
            placeholder="Enter contact number"
            defaultValue={contactForm?.contact_number}
            onBlur={onInputChange}
            containerClassName="flex flex-col"
            inputClassName="p-2 border my-2 rounded bg-transparent"
            error={formErrors.contact_number}
            errorClassName="text-xs"
          />
          <InputField
            id="contact-title"
            type="tel"
            key={contactForm?.job_title || "job_title"}
            name={"job_title"}
            placeholder="Enter Title"
            defaultValue={contactForm?.job_title}
            onBlur={onInputChange}
            containerClassName="flex flex-col"
            inputClassName="p-2 border my-2 rounded bg-transparent"
            error={formErrors.job_title}
            errorClassName="text-xs"
          />
          <div className="flex flex-col">
            <label className="mb-1 font-medium text-gray-700">
              <span className="text-red-500">*</span> Select Company
            </label>

            {!selectOtherCompany && (
              <CompanySearchBasic
                defaultValue={
                  contactForm?.company_id?._id || contactForm?.company_id
                }
                onSearchChange={(e) =>
                  onInputChange({
                    target: { name: "company_id", value: e },
                  })
                }
              />
            )}

            <div className="flex items-center mt-2">
              <input
                type="checkbox"
                id="company-not-listed"
                name="company-not-listed"
                checked={selectOtherCompany}
                onChange={(e) => handleCompanyNotListed(e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="active" className="font-medium text-gray-700">
                Company Not Listed here
              </label>
            </div>

            {selectOtherCompany && (
              <CreateCompany onCompanyChange={onCompanyChange} />
            )}

            {formErrors.company_id && (
              <div className="text-xs text-red-500">
                {formErrors.company_id}
              </div>
            )}
          </div>

          <div className="flex flex-col">
            <label className="mb-1 font-medium text-gray-700">
              Select Category
            </label>

            {!selectOtherCategory && (
              <CategorySearchBasic
                defaultValue={contactForm?.category_ids}
                onSearchChange={(e) =>
                  onInputChange({
                    target: { name: "category_ids", value: e },
                  })
                }
              />
            )}

            <div className="flex items-center my-2">
              <input
                type="checkbox"
                id="category-not-listed"
                name="category-not-listed"
                checked={selectOtherCategory}
                onChange={(e) => handleCategoryNotListed(e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="active" className="font-medium text-gray-400">
                Category not listed here
              </label>
            </div>

            {selectOtherCategory && (
              <CreateCategory
                onCategoryChange={onCategoryChange}
                className="my-2"
              />
            )}

            {formErrors.category_ids && (
              <div className="text-xs text-red-500">
                {formErrors.category_ids}
              </div>
            )}
          </div>

          <InputField
            id="contact-notes"
            inputType="textarea"
            rows={4}
            key={contactForm?.notes || "notes"}
            name={"notes"}
            placeholder="Enter notes"
            defaultValue={contactForm?.notes}
            onBlur={onInputChange}
            containerClassName="flex flex-col"
            inputClassName="p-2 border my-2 rounded bg-transparent"
            error={formErrors.notes}
            errorClassName="text-xs"
          />
        </>
      )}
      {formErrors.submit_error && (
        <div className="text-xs text-red-500">{formErrors.submit_error}</div>
      )}
    </Modal>
  );
};

export default ContactModal;
